import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ILensesCardViewProps } from './lenses-card.models';
import { useStyles } from './lenses-card.styles';
import { LensesBrandLogo } from '../../../../../common';
import { EyeParams } from '../../../../../common/eye-params';
import { useEyeParamsByEye } from '../../../../../../hooks/use-eye-params-by-eye';
import { NoveltyLabel } from '../../../../../common/novelty-label';
import { LensesBrandType, SolutionsBrandType } from '../../../../../../graphql/lenses/models/get-lenses.models';
import { checkLabelEnabled } from '../utils/check-label-enabled';
import { OutOfSalesLabel } from '../../../out-of-sales';
import { ProfileLabel } from '../../../profile-label';
import { ProductOwnerType } from '../../../../../../graphql/order/models';

const TOP_LABEL_BRAND_HEIGHT = 92;
const TOP_PADDING = '2.5rem';
const EXTRA_PADDING_STYLE = { paddingTop: TOP_PADDING };

export const LensesCard = ({
  cardData,
  profileFirstName,
  profileLastName,
  profileIsActive = true,
  profileIsDeleted = false,
  profileColorIndex,
  hasKidProfiles = false,
  isExtraPadding,
}: ILensesCardViewProps): JSX.Element => {
  const classes = useStyles();
  const {
    brandId,
    leftEye,
    rightEye,
    brand: { labels },
  } = cardData;
  const { leftEyeColumnData, rightEyeColumnData, isOneEye } = useEyeParamsByEye(leftEye, rightEye);
  const [isLabelEnabled, setIsLabelEnabled] = useState<boolean>(false);
  const [labelName, setLabelName] = useState<string>('');
  const intl = useIntl();

  useEffect(() => {
    if (labels.length) {
      const label = labels.find(checkLabelEnabled);
      if (label && label.name.length) {
        setLabelName(label.name);
        setIsLabelEnabled(true);
      }
    }
  }, [labels]);

  const isSolutionsBrand = cardData.brandId === SolutionsBrandType.arl;
  const isProductLabel = !isSolutionsBrand && hasKidProfiles;

  // Custom height for aomax1dfa
  const isTopLabelBrand = cardData.brandId === LensesBrandType.aomax1dfa || cardData.brandId === LensesBrandType.aomax1dmffa;
  const isExtraTopPadding = isExtraPadding && !hasKidProfiles;

  return (
    <li
      className={classes.root}
      data-testid="lenses-card"
      {...(isExtraTopPadding && { style: EXTRA_PADDING_STYLE })}
    >
      {isProductLabel && (
        <ProfileLabel
          profile={{
            firstName: profileFirstName || '',
            lastName: profileLastName || '',
            isActive: profileIsActive,
            ownerType:
              profileFirstName === 'Consumer'
                ? ProductOwnerType.consumer
                : ProductOwnerType.dependent,
            colorIndex: profileColorIndex,
          }}
          isDeleted={profileIsDeleted}
          isExtraPadding={isExtraPadding && hasKidProfiles}
          isLenses
        />
      )}
      <div>
        <LensesBrandLogo
          className={classes.lensesBrandLogo}
          brand={brandId as LensesBrandType}
          customHeight={isTopLabelBrand ? TOP_LABEL_BRAND_HEIGHT : undefined}
        />
      </div>
      <div className={classes.eyeParamsContainer}>
        <EyeParams {...rightEyeColumnData} />
        {!isOneEye && <EyeParams {...leftEyeColumnData} />}
      </div>
      {isLabelEnabled && <NoveltyLabel label={labelName} />}
      {brandId === LensesBrandType.oneDate && (
        <OutOfSalesLabel
          message={intl.formatMessage(
            {
              id: 'lenses.1DATE.outOfSales.message',
            },
            {
              sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
            },
          )}
          messageStyles={classes.messageStyles}
        />
      )}
    </li>
  );
};
